// Pure helpers for printing tray labels on a thermal printer. A tray label
// carries only the tray name and one code (barcode or QR) of the tray's
// barcode number — what the drop list / pick list scanners look trays up by.

export const TRAY_LABEL_SETTINGS_KEY = "trayLabelPrintSettings";

export const CODE_TYPES = ["barcode", "qr"];

// Same default label as GRN thermal labels — fits 58mm paper (48mm printable).
export const TRAY_LABEL_DEFAULTS = {
  codeType: "barcode",
  thermalLabelWidth: 48,
  thermalLabelHeight: 25,
  thermalGap: 0.2,
};

// Shared by the preview, the print-dialog fallback and the TSPL job, so every
// path encodes the same bars. A whole-number module width keeps bar edges
// crisp; margin is CODE128's 10-module quiet zone.
export const TRAY_BARCODE_RENDER_OPTIONS = {
  format: "CODE128",
  width: 2,
  height: 40,
  displayValue: false,
  margin: 20,
};

/** What the barcode / QR encodes: the tray's barcode number as plain digits. */
export const trayCodeValue = (tray) => String(tray?.barcode ?? "").trim();

const positiveOr = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
};

/** Label size and gap in mm, falling back to the defaults for unusable input. */
export const trayLabelSize = (values) => ({
  width: positiveOr(values?.thermalLabelWidth, TRAY_LABEL_DEFAULTS.thermalLabelWidth),
  height: positiveOr(values?.thermalLabelHeight, TRAY_LABEL_DEFAULTS.thermalLabelHeight),
  gap: positiveOr(values?.thermalGap, 0),
});

/**
 * One entry per label to print, in list order: each tray repeated by its label
 * count. Trays without a barcode have nothing to encode, so they are left out.
 */
export const trayLabelQueue = (trays, labelCounts) =>
  trays.flatMap((tray, index) => {
    const count = Math.max(0, Math.floor(Number(labelCounts?.[index]) || 0));
    return trayCodeValue(tray) ? Array(count).fill(tray) : [];
  });

/** Case-insensitive match on tray name or barcode; an empty query matches all. */
export const matchesTraySearch = (tray, query) => {
  const needle = String(query ?? "").trim().toLowerCase();
  if (!needle) return true;
  return [tray?.tray_name, tray?.barcode].some((field) =>
    String(field ?? "").toLowerCase().includes(needle)
  );
};

// Keeps only the known settings, each with a usable value.
const pickSettings = (raw) => {
  const settings = { ...TRAY_LABEL_DEFAULTS };
  if (CODE_TYPES.includes(raw?.codeType)) settings.codeType = raw.codeType;
  ["thermalLabelWidth", "thermalLabelHeight", "thermalGap"].forEach((key) => {
    const rawValue = raw?.[key];
    const value = Number(rawValue);
    if (rawValue != null && rawValue !== "" && Number.isFinite(value) && value >= 0) {
      settings[key] = value;
    }
  });
  return settings;
};

/** The saved default settings on this browser, or the defaults. */
export const readTrayLabelSettings = () => {
  try {
    const raw = localStorage.getItem(TRAY_LABEL_SETTINGS_KEY);
    return raw ? pickSettings(JSON.parse(raw)) : { ...TRAY_LABEL_DEFAULTS };
  } catch {
    return { ...TRAY_LABEL_DEFAULTS };
  }
};

/** Saves the label settings (not the per-tray counts); false if storage failed. */
export const saveTrayLabelSettings = (values) => {
  try {
    localStorage.setItem(TRAY_LABEL_SETTINGS_KEY, JSON.stringify(pickSettings(values)));
    return true;
  } catch {
    return false;
  }
};
